import { useEffect } from 'react';
import { db, serverTimestamp } from '../firebase/collections';

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Hook to automatically track and update the authenticated user's online status and last seen
 * @param {Object} user - Firebase auth user
 */
export function useUserPresence(user) {
  useEffect(() => {
    if (!user || !user.uid) return;

    const userRef = db.collection('users').doc(user.uid);

    // Update presence to online
    const markOnline = () => {
      userRef
        .update({
          isOnline: true,
          lastSeen: serverTimestamp(),
        })
        .catch(() => {});
    };

    // Update presence to offline / not viewing the site
    const markOffline = () => {
      userRef
        .update({
          isOnline: false,
          lastSeen: serverTimestamp(),
        })
        .catch(() => {});
    };

    // If currently visible, mark online immediately
    if (document.visibilityState === 'visible') {
      markOnline();
    }

    // Periodic heartbeat while active and visible
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        markOnline();
      }
    }, HEARTBEAT_INTERVAL_MS);

    // Handle tab visibility changes immediately (switching tabs or minimizing)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markOnline();
      } else {
        markOffline();
      }
    };

    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        markOnline();
      }
    };

    const handleBlur = () => {
      if (document.visibilityState !== 'visible') {
        markOffline();
      }
    };

    const handlePageHide = () => {
      markOffline();
    };

    const handleBeforeUnload = () => {
      markOffline();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      markOffline();
    };
  }, [user]);
}
